import {Fragment, ReactElement, useEffect, useState} from "react"
import {useSwipeable} from "react-swipeable"
import {Input, Button, Slider} from "antd"
import {observer} from "mobx-react-lite"
import {t} from "@ui/common/intl"
import store, {useStore} from "@ui/stores"
import {PoiCategory} from "@ui/stores/service/poi"
import {SwipeTouchEvent} from "@ui/components/services/common/SwipePanel"
import mapService from "@ui/services/MapService"
import poiService from "@ui/services/PoiService"
import SearchIcon from "@ui/icons/common/Search"
import BookmarkIcon from "@ui/icons/track/Bookmark"
import BookmarkOffIcon from "@ui/icons/track/BookmarkOff"
import NautilusIcon from "@ui/icons/common/Nautilus"

type TFilterProps = {
  onSearch: () => void
}

const maxRadius = 20000
const minRadius = 1000
const pow = (val: number = 0) => Math.pow(2, val);
const log = (val: number = 0) => Math.log2(val);
const convert = (radius: number) => {
  const val = Math.round(pow(radius / 10))
  return val <= minRadius ? minRadius : val >= maxRadius ? undefined : val
}
const getCenter = (): [number, number] => {
  const [width, height] = mapService.getMapSize() || []
  return mapService.getCoordinateFromPixel([
    store.settings.isMobile ? width / 2 : width * 2 / 3,
    height / 2
  ]) as [number, number]
}

const Filter = ({onSearch}: TFilterProps): ReactElement => {
  const {poi: {filter}, settings} = useStore()
  const [slider, setSlider] = useState(log(filter.params.within ? filter.params.within[2] : maxRadius) * 10)

  useEffect(() => {
    const valNew = convert(slider)
    const [xNew, yNew] = getCenter()
    const [x, y, val] = filter.params.within || []
    if (!val || (x === xNew && y === yNew && val === valNew)) return
    poiService.setFilterPoint(getCenter(), val)
    filter.change({within: [...getCenter(), val]})
    onSearch()
  }, [])

  useEffect(() => {
    return mapService.addCenterChangedCallback(() => {
      const val = convert(slider)
      if (!val) return
      poiService.setFilterPoint(getCenter(), val)
    })
  }, [slider])

  useEffect(() => {
    return mapService.addMoveEndCallback(() => {
      const val = convert(slider)
      if (!val) return
      filter.change({within: [...getCenter(), val]})
      onSearch()
    })
  }, [slider])

  const handlers = settings.isDesktop
    ? {}
    : useSwipeable({
        onSwipedLeft: ({event}) => {
          (event as SwipeTouchEvent).disableSwipe = true
        },
      })

  const handleToggleFavorite = () => {
    filter.change({favorite: !filter.params.favorite || undefined})
    onSearch()
  }

  const handleToggleUser = () => {
    filter.change({spot_type: filter.params.spot_type === PoiCategory.USER ? undefined : PoiCategory.USER})
    onSearch()
  }

  const handleSearch = (name: string) => {
    filter.change({name})
    onSearch()
  }

  const handleSliderChange = (value: number) => {
    setSlider(value)
    // @todo вызывает неадекватную работу слайдера на десктопе
    // const val = convert(value)
    // if (!val) poiService.clearFilter()
    // else poiService.setFilterPoint(getCenter(), val)
  }

  const handleSliderChangeComplete = (value: number) => {
    setSlider(value)
    const val = convert(value)
    if (!val) {
      filter.change({within: undefined})
      poiService.clearFilter()
    } else {
      const [x, y] = getCenter()
      filter.change({within: [x, y, val]})
      poiService.setFilterPoint([x, y], val)
    }
    onSearch()
  }

  return (
    <div className="filter">
      <div className="row">
        <Input.Search
          placeholder={t("track.list.filter.placeholder")}
          addonAfter={null}
          prefix={<SearchIcon/>}
          value={filter.params.name}
          onChange={(e) => filter.change({name: e.target.value})}
          onSearch={handleSearch}
          allowClear={true}
          maxLength={50}
        />
        <Button
          icon={
            filter.params.spot_type === PoiCategory.USER
              ? <NautilusIcon color="#0960A3" fill="#0960A355"/>
              : <NautilusIcon/>
          }
          onClick={handleToggleUser}
        />
        <Button
          icon={filter.params.favorite ? <BookmarkIcon/> : <BookmarkOffIcon/>}
          onClick={handleToggleFavorite}
        />
      </div>
      <div className="row" {...handlers}>
        <Slider
          min={Math.floor(log(minRadius) * 10)}
          max={Math.ceil(log(maxRadius) * 10)}
          step={1}
          value={slider}
          tooltip={{defaultOpen: false, open: false}}
          onChange={handleSliderChange}
          onChangeComplete={handleSliderChangeComplete}
        />
        <div className="radius">
          {
            [convert(slider)].map((value, i) =>
              <Fragment key={`slider-data-${i}`}>
                {value
                  ? t("poi.filter.radius.meter", {value: value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")})
                  : t("poi.filter.all.map.title")
                }
              </Fragment>
            )
          }
        </div>
      </div>
      </div>
      )
      }

      export default observer(Filter)
